// eslint-disable-next-line import/no-extraneous-dependencies
import InfoIcon from '@mui/icons-material/Info';
// eslint-disable-next-line import/no-extraneous-dependencies
import PreviewIcon from '@mui/icons-material/Preview';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AxiosResponse } from 'axios';
import { Image } from 'mui-image';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import { useBoolean } from 'usehooks-ts';
import { Button, ConfirmationDialog } from '@common-components';
import { api, ApiKey } from 'api';
import webAppDemoIcon from 'assets/images/gifs/web_app_demo.gif';
import robotSleepImage from 'assets/images/robot-sleep.svg';
import { useMenu, useServerError, useToast } from 'hooks';
import LinkBox from 'pages/app-pages/chatbot-builder-page/dialogs/publish/LinkBox';
import { SubmitButton } from 'pages/auth-pages/components';
import { useDirtyStore } from 'store';
import { Project } from 'types';

const WebAppMenuButton = ({ project }: { project?: Project }) => {
  const [url, setUrl] = useState(
    project?.botMenuButton?.type === 'web_app' ? project.botMenuButton.web_app.url : '',
  );
  const [text, setText] = useState(
    project?.botMenuButton?.type === 'web_app' ? project.botMenuButton.text : '',
  );
  const [urlError, setUrlError] = useState('');
  const toast = useToast();
  const toastServerError = useServerError();
  const { id } = project ?? {};
  const { openMenu: openUnsetPrompt, ...restUnsetPrompt } = useMenu();

  const initialUrl =
    project?.botMenuButton?.type === 'web_app' ? project.botMenuButton.web_app.url : '';
  const initialText = project?.botMenuButton?.type === 'web_app' ? project.botMenuButton.text : '';

  const hasChanges = url !== initialUrl || text !== initialText;

  const validateUrl = (urlItem: string): boolean => {
    try {
      const urlObj = new URL(urlItem);
      // Check if it's HTTPS
      if (urlObj.protocol !== 'https:') {
        return false;
      }
      // Check if it has a valid hostname (at least one dot and valid TLD)
      const { hostname } = urlObj;
      if (!hostname.includes('.') || hostname.split('.').pop()?.length === 0) {
        return false;
      }
      // Check if it has a valid path (optional)
      if (urlObj.pathname === '') {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrl(newUrl);

    if (!newUrl) {
      setUrlError('');
      return;
    }

    if (!validateUrl(newUrl)) {
      setUrlError(
        'Please enter a valid HTTPS URL with domain and path (e.g., https://example.com/app)',
      );
    } else {
      setUrlError('');
    }
  };

  const handleSetWebApp = async () => {
    if (!validateUrl(url)) {
      setUrlError(
        'Please enter a valid HTTPS URL with domain and path (e.g., https://example.com/app)',
      );
      return;
    }

    try {
      const menuButton = {
        type: 'web_app',
        text,
        web_app: {
          url,
        },
      };

      await api.patch(`${ApiKey.Project}/${id}/set-chat-menu-button`, menuButton);
      await mutate(`${ApiKey.Project}/${id}`);

      toast({
        message: 'Web App Menu Button set successfully',
        variant: 'success',
      });
    } catch (error: any) {
      toastServerError(error);
    }
  };

  const handleUnset = async () => {
    try {
      await api.patch(`${ApiKey.Project}/${id}/set-chat-menu-button`, {
        type: 'default',
      });
      await mutate(`${ApiKey.Project}/${id}`);

      setUrl('');
      setText('');
      setUrlError('');
      toast({
        message: 'Web App Menu Button unset successfully',
        variant: 'success',
      });
    } catch (error: any) {
      toastServerError(error);
    }
  };

  const handleParamsToWebAppChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const isChecked = event.target.checked;

      // If checking the box and there's a web app URL, add projectId to the URL
      if (isChecked && project?.botMenuButton?.type === 'web_app') {
        const currentUrl = project.botMenuButton.web_app.url;
        const urlObj = new URL(currentUrl);

        // Add projectId as query parameter
        urlObj.searchParams.set('projectId', id || '');
        const updatedUrl = urlObj.toString();

        // Update the web app URL with the projectId parameter
        const menuButton = {
          type: 'web_app',
          text: project.botMenuButton.text,
          web_app: {
            url: updatedUrl,
          },
        };

        await api.patch(`${ApiKey.Project}/${id}/set-chat-menu-button`, menuButton);
        setUrl(updatedUrl); // Update local state
      }
      // If unchecking the box and there's a web app URL, remove projectId from the URL
      else if (!isChecked && project?.botMenuButton?.type === 'web_app') {
        const currentUrl = project.botMenuButton.web_app.url;
        const urlObj = new URL(currentUrl);

        // Remove projectId query parameter
        urlObj.searchParams.delete('projectId');
        const updatedUrl = urlObj.toString();

        // Update the web app URL without the projectId parameter
        const menuButton = {
          type: 'web_app',
          text: project.botMenuButton.text,
          web_app: {
            url: updatedUrl,
          },
        };

        await api.patch(`${ApiKey.Project}/${id}/set-chat-menu-button`, menuButton);
        setUrl(updatedUrl); // Update local state
      }

      // Update the project setting
      await api.patch(`${ApiKey.Project}/${id}`, {
        addParamsToWebApp: isChecked,
      });
      await mutate(`${ApiKey.Project}/${id}`);

      toast({
        message: `Project params ${isChecked ? 'enabled' : 'disabled'} for WebApp`,
        variant: 'success',
      });
    } catch (error: any) {
      toastServerError(error);
    }
  };

  return (
    <Stack gap={1}>
      <Stack direction='row' alignItems='center' gap={1}>
        <Typography variant='subtitle1' component='h2'>
          Web App Menu Button
        </Typography>
        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant='caption' sx={{ display: 'block', mb: 1 }}>
                Preview of how the web app menu button appears in Telegram:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Image
                  src={webAppDemoIcon}
                  alt='Web App Menu Button Preview'
                  width={480}
                  height={360}
                  fit='contain'
                  duration={0}
                />
              </Box>
            </Box>
          }
          placement='top'
        >
          <IconButton size='small'>
            <PreviewIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Stack>
      <Paper sx={{ p: 2, width: '100%', border: 1, borderColor: 'divider' }}>
        <Stack gap={2}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              label='Web App URL'
              value={url}
              onChange={handleUrlChange}
              required
              fullWidth
              size='small'
              error={!!urlError}
              helperText={urlError}
              sx={{ position: 'relative' }}
            />
            <Tooltip
              title='The URL must be HTTPS and point to your web application. This URL will be opened when users click the menu button in Telegram.'
              placement='top'
            >
              <IconButton
                size='small'
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 3,
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                }}
              >
                <InfoIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            label='Button Text'
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            fullWidth
            size='small'
          />
          <Stack direction='row' gap={1}>
            <Button
              variant='contained'
              onClick={handleSetWebApp}
              disabled={!url || !text || !!urlError || !hasChanges}
              fullWidth
            >
              Set Web App
            </Button>
            {(url || text) && (
              <Button variant='outlined' onClick={openUnsetPrompt} fullWidth>
                Unset
              </Button>
            )}
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={project?.addParamsToWebApp || false}
                onChange={handleParamsToWebAppChange}
                size='small'
              />
            }
            label='Include Project Params in WebApp'
            sx={{ alignSelf: 'flex-start' }}
          />
          <Typography variant='caption' color='text.secondary'>
            Note: Changes to the menu button may take a few minutes to appear in Telegram.
          </Typography>
        </Stack>
      </Paper>
      <ConfirmationDialog
        text='Are you sure you want to unset the Web App Menu Button? This will remove the button from your Telegram bot.'
        onConfirm={handleUnset}
        {...restUnsetPrompt}
      />
    </Stack>
  );
};

const BotSettings = ({ project, isLoading }: { project?: Project; isLoading: boolean }) => {
  const { id } = project ?? {};
  const navigate = useNavigate();
  const { setIsDirty } = useDirtyStore((state) => ({ setIsDirty: state.setIsDirty }));
  const toastServerError = useServerError();
  const toast = useToast();
  const { value: revoking, setTrue: startRevoking, setFalse: stopRevoking } = useBoolean(false);
  const { openMenu: openRevokePrompt, ...restRevokePrompt } = useMenu();

  const handleOnRevokeToken = async () => {
    setIsDirty(false);
    startRevoking();
    try {
      await api.patch<Project, AxiosResponse<Project>, Partial<Project>>(
        `${ApiKey.Project}/${id}`,
        {
          token: '',
        },
      );
      await mutate(`${ApiKey.Project}/${id}`);
      toast({
        message: 'Bot token revoked successfully',
        variant: 'success',
      });

      stopRevoking();
    } catch (error: any) {
      stopRevoking();
      toastServerError(error);
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Stack width={1} alignItems='flex-start'>
        {project?.token ? (
          <Stack width={1} gap={5}>
            {project?.botUsername && (
              <Stack gap={2}>
                <LinkBox
                  value={`https://t.me/${project.botUsername}`}
                  label='Direct Bot Link'
                  copyText='Bot Link copied'
                  icon='smart_toy'
                  infoIcon
                  helpText='This is the direct link to your bot. Share it with your users to start a conversation.'
                />
                <LinkBox
                  value={project?.trackingUrl || ''}
                  copyText='Magic Link copied'
                  label='Magic Link'
                  icon='link'
                  infoIcon
                  helpText={`This magic link captures the visitor's IP, browser information, and any additional URL parameters before redirecting to your Telegram bot. You can utilize this data within your chatbot builder and forward it to your Webhook for enhanced interaction.`}
                />
              </Stack>
            )}
            <Stack gap={2}>
              <LinkBox
                value={`**** **** **** ${project?.token}`}
                label='Bot Token'
                icon='lock'
                hideCta
                //   helpText='Share this link to collect query parameters from url, send them to your bot and start a conversation.'
              />
              <Box>
                <SubmitButton
                  submitting={revoking}
                  variant='outlined'
                  fullWidth
                  size='medium'
                  onClick={openRevokePrompt}
                  color='error'
                >
                  Revoke Token
                </SubmitButton>
              </Box>
            </Stack>
            <Stack>
              <WebAppMenuButton project={project} />
            </Stack>
          </Stack>
        ) : (
          <Stack width={1} alignItems='center' gap={2}>
            <Stack width={1} alignItems='center' bgcolor='grey.50' borderRadius={1} p={2}>
              <Image width={200} duration={0} src={robotSleepImage} />
            </Stack>
            <Button
              fullWidth
              variant='outlined'
              size='medium'
              color='primary'
              onClick={() => {
                navigate(`publish`);
              }}
            >
              Activate Bot Token
            </Button>
          </Stack>
        )}
      </Stack>
      <ConfirmationDialog
        submittingText='Revoking...'
        text='Are you sure you want to revoke the bot token? It will disconnect the bot from Telegram.'
        onConfirm={handleOnRevokeToken}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        {...restRevokePrompt}
      />
    </>
  );
};

export default BotSettings;
